import React, { FC, useCallback, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';
import { useSelector } from 'react-redux';
import { Store } from '@/redux';
import { useAlert } from '@/shared/components';
import { ThrowCardProvider } from '@/screens/authenticated/in-play/context';
import { SendWSMessageProvider } from '@/screens/authenticated/send-message-ctx';
import { RenderGamePlay } from '@/screens/authenticated/in-play/components/playground';
import { TurnBanner } from '@/screens/authenticated/in-play/components/turn-banner';
import { useInPlayEngine } from '@/screens/authenticated/in-play/use-in-play-engine';
import { TableOptionsProvider } from '@/screens/authenticated/in-play/table-options';
import { Brand, GuestShell, LoadingState } from '../components/guest-shell';
import { Lobby } from '../components/lobby';
import { ResultsPanel } from '../components/results-panel';
import { Modal } from '../components/modal';
import { HowToPlaySteps } from '../components/how-to-play';
import { NameForm } from '../components/name-form';
import { sundayTheme } from '../sunday-theme';
import {
  apiErrorMessage,
  applyToken,
  inviteBots,
  leaveTable,
  refreshGuestUser,
  rematchTable,
  startGuestSession,
  useSessionInfo,
} from '../session';
import { isGameOver, parseLastTurn } from '../lib/turn';
import { shortTableCode } from '../lib/invite';
import { GUEST_ROUTES } from '../routes.definitions';
import '@/styles/sunday-table.css';

const noopGameOver = () => undefined;

const GuestTableInner: FC = () => {
  const nav = useNavigate();
  const { addAlert } = useAlert();
  const { token, name, roomID } = useSessionInfo();
  const engine = useInPlayEngine();
  const {
    inPlayResponse,
    gameInfo,
    socketError,
    connectionState,
    reconnect,
    sendMessage,
    selectedCard,
    setSelectedCard,
    onClickCard,
    userDeck,
    lockDrag,
    isLandscapeMode,
    userID,
  } = engine;

  const players = useMemo(
    () => inPlayResponse?.players || [],
    [inPlayResponse?.players]
  );
  const room = gameInfo?.room || null;
  const isHost = room?.role === 'owner';
  const hostName =
    inPlayResponse?.ownerUsername ||
    [...players].sort((a, b) => a.entryNo - b.entryNo)[0]?.name;

  const [confirmLeave, setConfirmLeave] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [resultsDismissedFor, setResultsDismissedFor] = useState<string | null>(
    null
  );

  const gameOver = isGameOver({
    userID,
    room,
    inPlayCards: gameInfo?.inPlayCards,
    userDeck: gameInfo?.userDeck,
    players,
  });
  const loser = parseLastTurn(room?.lastTurnResult).userLost;
  const resultsKey = gameOver
    ? `${room?.roomID}:${loser}:${room?.updatedAt}`
    : null;
  const showResults = gameOver && resultsDismissedFor !== resultsKey;

  const doLeave = useCallback(async () => {
    if (!token) return false;
    setLeaving(true);
    try {
      const lobbyToken = await leaveTable(token);
      sendMessage('left-room');
      await applyToken(lobbyToken);
      nav(GUEST_ROUTES.HOME, { replace: true });
      return true;
    } catch (err) {
      setLeaving(false);
      addAlert({
        type: 'error',
        message: apiErrorMessage(err, 'Could not leave the table. Try again.'),
      });
      return false;
    }
  }, [addAlert, nav, sendMessage, token]);

  const onRematch = useCallback(async () => {
    if (!token) return;
    try {
      await rematchTable(token);
      sendMessage('rematch');
      // same as the account flow: reload so every engine ref starts fresh
      window.location.assign(GUEST_ROUTES.TABLE);
    } catch (err) {
      addAlert({
        type: 'error',
        message: apiErrorMessage(err, 'Could not start a rematch.'),
      });
    }
  }, [addAlert, sendMessage, token]);

  const onFillWithBots = useCallback(async () => {
    if (!token || !room) return;
    const empty = room.numberOfPlayers - players.length;
    if (empty <= 0) return;
    try {
      await inviteBots(token, empty);
      sendMessage('ping');
    } catch (err) {
      addAlert({
        type: 'error',
        message: apiErrorMessage(err, 'Could not seat bots right now.'),
      });
    }
  }, [addAlert, players.length, room, sendMessage, token]);

  const onRetryConnection = useCallback(async () => {
    if (!token) return;
    // An expired guest token also looks like a dead socket: check first.
    const res = await refreshGuestUser(token, {
      kind: 'guest',
      name,
      createdAt: 0,
    });
    if (res === 'ok') reconnect();
  }, [name, reconnect, token]);

  const tableOptions = useMemo(
    () => ({ isGuestTable: true, onGameOver: noopGameOver }),
    []
  );

  const leaveModal = confirmLeave && (
    <Modal title="Leave this table?" onClose={() => setConfirmLeave(false)}>
      <p className="st-muted">
        {room?.isFull && !gameOver
          ? 'The game is in progress. Your seat stays empty and the others may be stuck waiting for you.'
          : 'You can come back with the invite link while seats are open.'}
      </p>
      <button
        type="button"
        className="st-btn st-btn--primary st-btn--block"
        onClick={() => setConfirmLeave(false)}
      >
        Stay at the table
      </button>
      <button
        type="button"
        className="st-btn st-btn--on-ivory st-btn--block"
        onClick={doLeave}
        disabled={leaving}
        aria-busy={leaving}
      >
        {leaving ? 'Leaving…' : 'Leave table'}
      </button>
    </Modal>
  );

  // ── connection states ──────────────────────────────────────────────────
  if (!inPlayResponse && !socketError) {
    return (
      <GuestShell title="Your table">
        <LoadingState
          label={
            connectionState === 'reconnecting'
              ? 'Reconnecting to your table…'
              : 'Pulling up your chair…'
          }
        />
      </GuestShell>
    );
  }

  if (!inPlayResponse && socketError) {
    return (
      <GuestShell title="Connection lost">
        <div className="st-card st-stack" data-suit="spades" role="alert">
          <h1 className="st-h2">We lost the table connection</h1>
          <p className="st-muted">
            Your seat is kept while you reconnect. If the problem continues,
            check your network.
          </p>
          <button
            type="button"
            className="st-btn st-btn--primary st-btn--block"
            onClick={onRetryConnection}
          >
            Reconnect
          </button>
          <button
            type="button"
            className="st-btn st-btn--on-ivory st-btn--block"
            onClick={() => setConfirmLeave(true)}
          >
            Leave table
          </button>
        </div>
        {leaveModal}
      </GuestShell>
    );
  }

  // ── lobby ──────────────────────────────────────────────────────────────
  if (!room?.isFull) {
    return (
      <GuestShell
        title="Lobby"
        right={
          connectionState !== 'open' ? (
            <span className="st-row st-small" role="status">
              <span className="st-dot st-dot--warn" aria-hidden="true" />
              Reconnecting…
            </span>
          ) : undefined
        }
      >
        <Lobby
          roomID={room?.roomID || roomID || ''}
          seats={room?.numberOfPlayers || 0}
          players={players}
          meID={userID}
          meName={name}
          isHost={isHost}
          hostName={hostName}
          onFillWithBots={onFillWithBots}
          onLeave={() => setConfirmLeave(true)}
        />
        {leaveModal}
      </GuestShell>
    );
  }

  // ── gameplay ───────────────────────────────────────────────────────────
  return (
    <TableOptionsProvider value={tableOptions}>
      <div className="st-app st-table">
        <header className="st-table__header">
          <Brand />
          <div className="st-table__meta">
            <span className="st-sr-only">Table</span>
            <span aria-label={`Table code ${shortTableCode(room.roomID)}`}>
              {shortTableCode(room.roomID)} · {room.numberOfPlayers} players
            </span>
          </div>
          <div className="st-row" style={{ gap: 6, flexWrap: 'nowrap' }}>
            <button
              type="button"
              className="st-btn st-btn--ghost st-btn--small st-icon-btn"
              aria-label="How to play"
              onClick={() => setShowRules(true)}
            >
              ?
            </button>
            <button
              type="button"
              className="st-btn st-btn--secondary st-btn--small"
              onClick={() => setConfirmLeave(true)}
            >
              Leave
            </button>
          </div>
        </header>

        <TurnBanner
          compact={isLandscapeMode}
          connectionState={connectionState}
          onReconnect={onRetryConnection}
        />

        <h1 className="st-sr-only">Bhabi table</h1>
        <div className="st-table__stage">
          <RenderGamePlay
            inPlayResponse={inPlayResponse}
            sendMessage={sendMessage}
            showPlayGround={Boolean(room.isFull && players.length)}
            selectedCard={selectedCard}
            gameInfo={gameInfo}
            setSelectedCard={setSelectedCard}
            userDeck={userDeck}
            userID={userID}
            lockDrag={lockDrag}
            onClickCard={onClickCard}
            isLandscapeMode={isLandscapeMode}
          />
        </div>

        {showResults && (
          <ResultsPanel
            players={players}
            loser={loser}
            draw={parseLastTurn(room?.lastTurnResult).draw}
            meID={userID}
            isHost={isHost}
            hostName={hostName}
            onRematch={onRematch}
            onLeave={doLeave}
            onNewTable={async () => {
              if (await doLeave()) nav(GUEST_ROUTES.NEW_TABLE);
            }}
            onClose={() => setResultsDismissedFor(resultsKey)}
          />
        )}
        {showRules && (
          <Modal title="How to play" onClose={() => setShowRules(false)}>
            <HowToPlaySteps />
            <button
              type="button"
              className="st-btn st-btn--primary st-btn--block"
              onClick={() => setShowRules(false)}
            >
              Back to the game
            </button>
          </Modal>
        )}
        {leaveModal}
      </div>
    </TableOptionsProvider>
  );
};

/** Expired / missing guest session: say so and offer a one-tap fresh seat. */
const ExpiredRecovery: FC = () => {
  const nav = useNavigate();
  const guestName = useSelector((s: Store) => s.userReducer.guestName) || '';
  const [error, setError] = useState<string | null>(null);

  return (
    <GuestShell title="Session expired">
      <div className="st-stack" style={{ ['--st-gap' as string]: '18px' }}>
        <p className="st-eyebrow">Guest session expired</p>
        <h1 className="st-h2">Your seat timed out</h1>
        <p className="st-lead">
          Guest seats last 7 days. That table can&apos;t be reopened, but you
          can be back at a new one in two taps.
        </p>
        <div className="st-card" data-suit="diamonds">
          <NameForm
            defaultName={guestName}
            submitLabel="Get a fresh seat"
            onSubmit={async n => {
              setError(null);
              try {
                await startGuestSession(n);
                nav(GUEST_ROUTES.NEW_TABLE, { replace: true });
              } catch (err) {
                setError(
                  apiErrorMessage(err, 'Could not start a new session.')
                );
              }
            }}
            serverError={error}
          />
        </div>
      </div>
    </GuestShell>
  );
};

export const TablePage: FC = () => {
  const { isAccount, isGuest, isInRoom, guestExpired, bootError } =
    useSessionInfo();

  if (isAccount) return <Navigate to="/in-play" replace />;
  if (guestExpired) return <ExpiredRecovery />;
  if (bootError === 'network' && !isGuest) {
    return (
      <GuestShell title="Offline">
        <div className="st-card st-stack" data-suit="spades" role="alert">
          <h1 className="st-h2">We can&apos;t reach the Bhabi server</h1>
          <p className="st-muted">
            Your seat is saved on this device. Reconnect and we&apos;ll put you
            straight back at the table.
          </p>
          <button
            type="button"
            className="st-btn st-btn--primary st-btn--block"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        </div>
      </GuestShell>
    );
  }
  if (!isGuest || !isInRoom) return <Navigate to={GUEST_ROUTES.HOME} replace />;

  return (
    <ThemeProvider theme={sundayTheme}>
      <SendWSMessageProvider>
        <ThrowCardProvider>
          <GuestTableInner />
        </ThrowCardProvider>
      </SendWSMessageProvider>
    </ThemeProvider>
  );
};
